import type {
  BaseStorage,
  BaseStorageOptions,
} from '@wix/fe-essentials/data-capsule';
import { Scope } from '@wix/data-capsule/dist/types/base-storage';
import merge from 'lodash/merge';

export interface DataCapsuleMergeStrategyParams {
  readonly storage: BaseStorage<BaseStorageOptions>;
}

// This "merge" layer allows to mimic the automatic "merge" behavior of app-settings also in user-preferences storage strategy.
// https://github.com/wix-private/app-settings/blob/64eef870a0958a117a8325511f57df9d73857359/app-settings-service/src/services/mysql-storage.ts#L273
export class DataCapsuleMergeStrategy
  implements BaseStorage<BaseStorageOptions>
{
  private readonly storage;

  constructor(params: DataCapsuleMergeStrategyParams) {
    this.storage = params.storage;
  }

  extendScope(scope: Scope) {
    return scope;
  }

  async setItem<T>(
    key: string,
    value: T,
    options: BaseStorageOptions,
  ): Promise<void> {
    const { storage } = this;
    const data = await this.getItem(key, options).catch(() => undefined);
    return storage.setItem(key, merge(data, value), options);
  }

  async removeItem(key: string, options: BaseStorageOptions) {
    const { storage } = this;
    const data = await this.getItem(key, options).catch(() => undefined);
    return storage.setItem(key, merge(data, { [key]: null }), options);
  }

  async getItem<T = any>(key: string, options: BaseStorageOptions): Promise<T> {
    return this.storage.getItem(key, options);
  }

  async getAllItems<T = any>(options: BaseStorageOptions): Promise<T> {
    return this.storage.getAllItems(options);
  }
}
